import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import assetService from '../services/assetService';
import skillService from '../services/skillService';
import { API_ENDPOINTS } from '../config/api';
import { FaTrash, FaImage, FaVideo } from 'react-icons/fa';
import '../styles/IssuesPage.css';
import AssistantSidebar from '../components/AssistantSidebar';

const CreateIssuePage = () => {
  const [assets, setAssets] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [skills, setSkills] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [form, setForm] = useState({ assetId: '', department: '', issueType: 'breakdown', description: '', requiredSkill: '', severity: 'medium', assignedTechnician: '', checklist: [], attachments: [] });
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [filePreview, setFilePreview] = useState([]);
  const navigate = useNavigate();
  const token = sessionStorage.getItem('token');

  const fetchAssets = useCallback(async () => {
    try {
      const res = await assetService.getAllAssets();
      const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setAssets(data);
    } catch (err) {
      console.error('Failed to load assets', err);
    }
  }, []);

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.DEPARTMENTS, { headers: { Authorization: token ? `Bearer ${token}` : '' } });
      const data = res.data?.data || res.data || [];
      setDepartments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load departments', err);
    }
  }, [token]);

  const fetchSkills = useCallback(async () => {
    try {
      const res = await skillService.getAllSkills();
      const data = res?.data || res || [];
      setSkills(Array.isArray(data) ? data : (data.data || []));
    } catch (err) {
      console.error('Failed to load skills', err);
    }
  }, []);

  const fetchTechnicians = useCallback(async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.AUTH_TECHNICIANS, { headers: { Authorization: token ? `Bearer ${token}` : '' } });
      const techs = res.data?.data || [];
      setTechnicians(techs);
    } catch (err) {
      console.error('Failed to load technicians', err);
    }
  }, [token]);

  useEffect(() => {
    fetchAssets();
    fetchDepartments();
    fetchSkills();
    fetchTechnicians();
  }, [fetchAssets, fetchDepartments, fetchSkills, fetchTechnicians]);

  const addChecklist = () => {
    if (!newChecklistItem) return;
    setForm(prev => ({ ...prev, checklist: [...prev.checklist, { text: newChecklistItem, completed: false }] }));
    setNewChecklistItem('');
  };

  const removeChecklist = (idx) => {
    setForm(prev => ({ ...prev, checklist: prev.checklist.filter((_, i) => i !== idx) }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 50 * 1024 * 1024; // 50MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'video/mp4', 'video/mpeg', 'video/quicktime'];

    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        setError(`File ${file.name} is too large. Max 50MB allowed.`);
        return false;
      }
      if (!allowedTypes.includes(file.type)) {
        setError(`File ${file.name} type not supported. Use JPG, PNG, GIF, MP4, MPEG, or MOV.`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Convert files to base64 and store
    const newAttachments = [];
    for (const file of validFiles) {
      const reader = new FileReader();
      reader.onload = () => {
        newAttachments.push({
          filename: file.name,
          data: reader.result,
          mimetype: file.type,
          size: file.size
        });
        
        if (newAttachments.length === validFiles.length) {
          setForm(prev => ({ ...prev, attachments: [...prev.attachments, ...newAttachments] }));
          setFilePreview(prev => [...prev, ...validFiles.map(f => ({
            name: f.name,
            type: f.type,
            size: (f.size / (1024 * 1024)).toFixed(2)
          }))]);
        }
      };
      reader.readAsDataURL(file);
    }

    e.target.value = ''; // Reset input
  };

  const removeAttachment = (idx) => {
    setForm(prev => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== idx) }));
    setFilePreview(prev => prev.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!form.assetId || !form.department) {
      setError('Asset and Department are required');
      return;
    }

    const payload = {
      assetId: form.assetId,
      department: form.department,
      issueType: form.issueType,
      description: form.description,
      severity: form.severity,
      requiredSkill: form.requiredSkill,
      assignedTechnician: form.assignedTechnician,
      checklist: form.checklist,
      attachments: form.attachments
    };

    try {
      setSaving(true);
      await axios.post(API_ENDPOINTS.ISSUES, payload, { headers: { Authorization: token ? `Bearer ${token}` : '' } });
      navigate('/issues');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to create issue');
    } finally { setSaving(false); }
  };

  return (
    <div className="create-issue-page">
      <AssistantSidebar user={null} />
      <div className="create-issue-page-container">
        <div className="create-issue-page-header">
        <h1>Create New Issue</h1>
        <p>Fill out the form below to create a new issue</p>
      </div>
      {error && <div className="create-issue-error-message">{error}</div>}
      <form onSubmit={submit} className="create-issue-modern-form">
        <div className="form-group">
          <label>Asset *</label>
          <select value={form.assetId} onChange={e => setForm({ ...form, assetId: e.target.value })} required className="form-select">
            <option value="">Select asset</option>
            {assets.map(a => (
              <option key={a._id} value={a._id}>{a.assetName} — {a.serialNumber || a._id}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Department *</label>
          <select value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} required className="form-select">
            <option value="">Select department</option>
            {departments.map(dept => (
              <option key={dept._id} value={dept._id}>{dept.name}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Issue Type *</label>
          <select value={form.issueType} onChange={e => setForm({ ...form, issueType: e.target.value })} required className="form-select">
            <option value="breakdown">Breakdown</option>
            <option value="preventive">Preventive</option>
            <option value="calibration">Calibration</option>
          </select>
        </div>

        <div className="form-group">
          <label>Required Skill *</label>
          <select value={form.requiredSkill} onChange={e => setForm({ ...form, requiredSkill: e.target.value })} required className="form-select">
            <option value="">Select skill</option>
            {skills.map(s => (
              <option key={s._id || s.name} value={s.name || s._id}>{s.label || s.name}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Assign Technician *</label>
          <select value={form.assignedTechnician} onChange={e => setForm({ ...form, assignedTechnician: e.target.value })} required className="form-select">
            <option value="">Select technician</option>
            {technicians.map(t => (
              <option key={t._id} value={t._id}>
                {t.name} — {t.email} [{t.availability === 'busy' ? '🔴 Busy' : '🟢 Available'}]
              </option>
            ))}
          </select>
          <small className="form-helper-text">
            🟢 Available = No active issues | 🔴 Busy = Currently working on issue
          </small>
        </div>

        <div className="form-group">
          <label>Severity *</label>
          <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })} required className="form-select">
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        <div className="form-group">
          <label>Description *</label>
          <textarea rows={6} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required className="form-textarea" />
        </div>

        <div className="form-group">
          <label>Upload Images/Videos (optional)</label>
          <p className="form-helper-text">Supported: JPG, PNG, GIF, MP4, MPEG, MOV (Max 50MB each)</p>
          <input 
            type="file" 
            multiple 
            accept="image/jpeg,image/png,image/gif,video/mp4,video/mpeg,video/quicktime"
            onChange={handleFileUpload}
            className="form-input-file"
          />
        </div>

        {filePreview.length > 0 && (
          <div className="file-preview-section">
            <label>Uploaded Files ({form.attachments.length})</label>
            <div className="file-preview-list">
              {filePreview.map((file, idx) => (
                <div key={idx} className="file-preview-item">
                  <div className="file-info">
                    {file.type.startsWith('image/') ? <FaImage className="file-icon-image" /> : <FaVideo className="file-icon-video" />}
                    <div>
                      <div className="file-name">{file.name}</div>
                      <div className="file-size">{file.size} MB</div>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    onClick={() => removeAttachment(idx)}
                    className="btn-remove-file"
                  >
                    <FaTrash /> Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label>Checklist (optional)</label>
          <div className="checklist-container">
            <div className="checklist-input-group">
              <input 
                placeholder="Add checklist item" 
                value={newChecklistItem} 
                onChange={e => setNewChecklistItem(e.target.value)} 
                className="form-input"
              />
              <button type="button" onClick={addChecklist} className="btn-add-checklist">Add</button>
            </div>
            {form.checklist.length > 0 && (
              <ul className="checklist-items">
                {form.checklist.map((c, idx) => (
                  <li key={idx} className="checklist-item">
                    <span>{c.text}</span>
                    <button type="button" onClick={() => removeChecklist(idx)} className="btn-remove-checklist">Remove</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" disabled={saving} className="btn-create-issue">
            {saving ? 'Creating...' : 'Create Issue'}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
};

export default CreateIssuePage;



